/**
 * LabTrades — recent trades across all lab strategies (PULSE-200).
 *
 * Left half of the LAB top row (ContainerPanel is the right half).
 * Filter by strategy via the dropdown; "Все стратегии" shows the
 * merged stream. PULSE-215: filter by COIN — pick a symbol to see all
 * its trades across every lab strategy, with a per-coin summary
 * (n / WR / avg / Σ PnL). Mirrors lab visual language.
 */
import { useState } from "react";

import {
  type LabTrade,
  useCreateTradeShare,
  useLabStrategies,
  useLabTradeSymbols,
  useLabTrades,
} from "@/hooks/useDashboardApi";
import { cn } from "@/lib/cn";
import { formatPct, timeAgo } from "@/lib/format";

export function LabTrades() {
  const [strategyId, setStrategyId] = useState<number | "">("");
  const [symbol, setSymbol] = useState<string>("");
  const { data: strats } = useLabStrategies();
  const { data: syms } = useLabTradeSymbols();
  const all = strategyId === "";
  const byCoin = symbol !== "";
  // When focusing a single coin, pull the full history, not just the top-60.
  const { data, isLoading, isError } = useLabTrades(
    all ? undefined : strategyId,
    symbol || undefined,
    byCoin ? 500 : 60,
  );

  const items = strats?.items ?? [];
  const coins = syms?.symbols ?? [];
  const trades = data?.trades ?? [];
  const summary = byCoin ? summarize(trades) : null;

  return (
    <div className="bg-ink border border-line flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-line">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-textDim whitespace-nowrap">
          Сделки
        </h2>
        <div className="flex items-center gap-1.5 min-w-0">
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="bg-surface border border-line text-xs text-text px-2 py-1 outline-none focus:border-pulse/50 max-w-[44%]"
            title="Фильтр по монете"
          >
            <option value="">Все монеты</option>
            {coins.map((c) => (
              <option key={c.symbol} value={c.symbol}>
                {c.symbol.replace("USDT", "")} · {c.trades}
              </option>
            ))}
          </select>
          <select
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value === "" ? "" : Number(e.target.value))}
            className="bg-surface border border-line text-xs text-text px-2 py-1 outline-none focus:border-pulse/50 max-w-[52%]"
            title="Фильтр по стратегии"
          >
            <option value="">Все стратегии</option>
            {items.map((s) => (
              <option key={s.strategy_id} value={s.strategy_id}>
                {s.name}{s.version != null ? ` v${s.version}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {summary && (
        <div className="flex items-center gap-4 px-4 py-2 border-b border-line bg-surface/40 text-xs">
          <span className="font-mono text-text">{symbol.replace("USDT", "")}</span>
          <Stat label="сделок" value={`${summary.n}`} />
          <Stat
            label="WR"
            value={summary.n ? `${Math.round(summary.wr * 100)}%` : "—"}
          />
          <Stat
            label="avg"
            value={summary.n ? formatPct(summary.avg, { sign: true }) : "—"}
            tone={summary.avg >= 0 ? "pulse" : "rose"}
          />
          <Stat
            label="Σ"
            value={summary.n ? formatPct(summary.sum, { sign: true }) : "—"}
            tone={summary.sum >= 0 ? "pulse" : "rose"}
          />
          {summary.open > 0 && (
            <span className="text-amber text-2xs ml-auto">{summary.open} открыто</span>
          )}
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight: 440 }}>
        {isLoading && <Empty text="…" />}
        {isError && <Empty text="Не удалось загрузить сделки." tone="rose" />}
        {data && trades.length === 0 && <Empty text="Сделок нет." />}
        {trades.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-ink z-10">
              <tr className="text-left text-textFaint border-b border-line">
                <th className="px-3 py-1.5 font-medium">Когда</th>
                {all && <th className="px-3 py-1.5 font-medium">Страта</th>}
                <th className="px-3 py-1.5 font-medium">Символ</th>
                <th className="px-3 py-1.5 font-medium">Аккаунт</th>
                <th className="px-3 py-1.5 font-medium text-right">PnL</th>
                <th className="px-3 py-1.5 font-medium">Статус</th>
                <th className="px-3 py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {trades.map((tr, i) => (
                <Row key={i} t={tr} showStrat={all} dimSymbol={byCoin} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function summarize(trades: LabTrade[]) {
  const closed = trades.filter((t) => !t.is_open && t.realized_pnl_pct != null);
  const n = closed.length;
  const sum = closed.reduce((a, t) => a + (t.realized_pnl_pct ?? 0), 0);
  const wins = closed.filter((t) => (t.realized_pnl_pct ?? 0) > 0).length;
  return {
    n,
    sum,
    avg: n ? sum / n : 0,
    wr: n ? wins / n : 0,
    open: trades.filter((t) => t.is_open).length,
  };
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "pulse" | "rose";
}) {
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-textFaint text-2xs uppercase tracking-wider">{label}</span>
      <span
        className={cn(
          "tabular-nums font-mono",
          tone === "pulse" ? "text-pulse" : tone === "rose" ? "text-rose" : "text-text",
        )}
      >
        {value}
      </span>
    </span>
  );
}

function Row({
  t,
  showStrat,
  dimSymbol,
}: {
  t: LabTrade;
  showStrat: boolean;
  dimSymbol: boolean;
}) {
  const pnl = t.is_open ? t.unrealized_pnl_pct : t.realized_pnl_pct;
  const dirShort = t.direction === "short";
  const share = useCreateTradeShare();
  const [copied, setCopied] = useState(false);
  const onShare = () => {
    if (t.position_id == null) return;
    share.mutate(t.position_id, {
      onSuccess: (d) => {
        navigator.clipboard?.writeText(window.location.origin + d.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
    });
  };
  return (
    <tr className="border-b border-line/40 hover:bg-line/20">
      <td className="px-3 py-1.5 text-textFaint whitespace-nowrap">
        {t.opened_at ? timeAgo(t.opened_at) : "—"}
      </td>
      {showStrat && (
        <td
          className="px-3 py-1.5 text-textDim truncate max-w-[130px]"
          title={t.strategy_name ?? ""}
        >
          {t.strategy_name ?? "—"}
        </td>
      )}
      <td className="px-3 py-1.5">
        <span className={cn("font-mono", dimSymbol && "text-textFaint")}>
          {t.symbol.replace("USDT", "")}
        </span>
        <span
          className={cn(
            "ml-1.5 text-2xs font-semibold",
            dirShort ? "text-rose" : "text-pulse",
          )}
        >
          {dirShort ? "S" : "L"}
        </span>
      </td>
      <td className="px-3 py-1.5">
        {t.account_label ? (
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-2xs font-semibold whitespace-nowrap",
              t.is_real
                ? "bg-rose/15 text-rose"
                : "bg-line/40 text-textFaint",
            )}
            title={t.is_real ? "Реальные деньги" : "Демо"}
          >
            {t.account_label}
          </span>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>
      <td
        className={cn(
          "px-3 py-1.5 text-right tabular-nums font-mono",
          pnl == null ? "text-textFaint" : pnl >= 0 ? "text-pulse" : "text-rose",
        )}
      >
        {pnl == null ? "—" : formatPct(pnl, { sign: true })}
      </td>
      <td className="px-3 py-1.5">
        {t.is_open ? (
          <span className="text-amber text-2xs">открыта</span>
        ) : (
          <span className="text-textFaint text-2xs">
            {t.exit_reason ?? "закрыта"}
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right">
        <button
          onClick={onShare}
          disabled={t.position_id == null || share.isPending}
          title="Поделиться сделкой — скопировать публичную ссылку"
          className="text-textFaint hover:text-pulse text-2xs disabled:opacity-40"
        >
          {copied ? "✓ ссылка" : share.isPending ? "…" : "Поделиться"}
        </button>
      </td>
    </tr>
  );
}

function Empty({ text, tone }: { text: string; tone?: "rose" }) {
  return (
    <div
      className={cn(
        "px-4 py-10 text-center text-sm",
        tone === "rose" ? "text-rose" : "text-textFaint",
      )}
    >
      {text}
    </div>
  );
}
